import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import {
  ArrowLeft, MonitorUp, Wifi, WifiOff, Loader2, Maximize2, Minimize2, MousePointer2, Power, Building2, Keyboard,
} from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const WS_BASE = BASE.replace(/^http/, 'ws');

/**
 * Viewer remoto del Gestore (FASE B): riceve i frame JPEG dell'agente via WebSocket
 * relay e invia gli eventi mouse/tastiera (coordinate normalizzate 0..1).
 */
export default function GestoreRemoteViewer() {
  const { t } = useTranslation();
  const { sid } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [wsState, setWsState] = useState('connecting'); // connecting | open | closed | ended
  const [agentOnline, setAgentOnline] = useState(false);
  const [meta, setMeta] = useState(null);
  const [fps, setFps] = useState(0);
  const [hasFrame, setHasFrame] = useState(false);
  const [control, setControl] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [ending, setEnding] = useState(false);

  const wsRef = useRef(null);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapRef = useRef(null);
  const lastUrlRef = useRef(null);
  const frameCountRef = useRef(0);
  const lastMoveRef = useRef(0);
  const controlRef = useRef(control);
  controlRef.current = control;
  const endedRef = useRef(false);

  // ---- stato sessione (polling di sicurezza) ----
  const loadSession = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/remote/support/manager/session/${sid}`);
      setSession(data);
      if (data.status !== 'active') { endedRef.current = true; setWsState('ended'); }
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 403) { endedRef.current = true; setWsState('ended'); }
    }
  }, [sid]);

  useEffect(() => {
    loadSession();
    const id = setInterval(loadSession, 5000);
    return () => clearInterval(id);
  }, [loadSession]);

  // ---- contatore fps ----
  useEffect(() => {
    const id = setInterval(() => { setFps(frameCountRef.current); frameCountRef.current = 0; }, 1000);
    return () => clearInterval(id);
  }, []);

  // ---- WebSocket viewer con riconnessione ----
  useEffect(() => {
    let closedByUs = false;
    let retryTimer = null;
    const token = localStorage.getItem('rmp_admin_token') || '';

    const connect = () => {
      if (endedRef.current) return;
      setWsState('connecting');
      const ws = new WebSocket(`${WS_BASE}/api/remote/ws/viewer/${sid}?token=${encodeURIComponent(token)}`);
      ws.binaryType = 'blob';
      wsRef.current = ws;
      ws.onopen = () => setWsState('open');
      ws.onmessage = (ev) => {
        if (ev.data instanceof Blob) {
          const url = URL.createObjectURL(ev.data);
          const img = imgRef.current;
          if (img) {
            const prev = lastUrlRef.current;
            img.onload = () => { if (prev) URL.revokeObjectURL(prev); };
            img.src = url;
            lastUrlRef.current = url;
          }
          frameCountRef.current += 1;
          setHasFrame(true);
          return;
        }
        try {
          const obj = JSON.parse(ev.data);
          if (obj.type === 'status') setAgentOnline(!!obj.agent_connected);
          else if (obj.type === 'meta') { setMeta(obj); setAgentOnline(true); }
          else if (obj.type === 'ended') { endedRef.current = true; setWsState('ended'); }
        } catch (e) { /* noop */ }
      };
      ws.onclose = (ev) => {
        wsRef.current = null;
        if (closedByUs) return;
        if (ev.code === 4000 || ev.code === 4404 || ev.code === 4403 || ev.code === 4401) {
          endedRef.current = true; setWsState('ended'); return;
        }
        setWsState('closed');
        retryTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => { /* onclose gestisce il retry */ };
    };
    connect();
    return () => {
      closedByUs = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { wsRef.current && wsRef.current.close(); } catch (e) { /* noop */ }
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
    };
  }, [sid]);

  // ---- fullscreen ----
  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggleFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  // ---- invio input ----
  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (!controlRef.current || !ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(obj));
  }, []);

  /** Coordinate normalizzate rispetto all'area effettiva dell'immagine (object-fit: contain). */
  const norm = (e) => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return null;
    const r = img.getBoundingClientRect();
    const scale = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
    const cw = img.naturalWidth * scale, ch = img.naturalHeight * scale;
    const ox = r.left + (r.width - cw) / 2, oy = r.top + (r.height - ch) / 2;
    const x = (e.clientX - ox) / cw, y = (e.clientY - oy) / ch;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x: Math.round(x * 10000) / 10000, y: Math.round(y * 10000) / 10000 };
  };

  const onMouseMove = (e) => {
    const now = performance.now();
    if (now - lastMoveRef.current < 33) return; // ~30 eventi/s
    const p = norm(e); if (!p) return;
    lastMoveRef.current = now;
    send({ t: 'mm', ...p });
  };
  const onMouseDown = (e) => { e.preventDefault(); overlayRef.current?.focus(); const p = norm(e); if (!p) return; send({ t: 'md', b: e.button, ...p }); };
  const onMouseUp = (e) => { e.preventDefault(); const p = norm(e); if (!p) return; send({ t: 'mu', b: e.button, ...p }); };
  const onWheel = (e) => { e.preventDefault(); send({ t: 'wheel', dx: Math.round(e.deltaX), dy: Math.round(e.deltaY) }); };
  const onKey = (type) => (e) => {
    if (!controlRef.current) return;
    e.preventDefault(); e.stopPropagation();
    send({ t: type, key: e.key, code: e.code, ctrl: e.ctrlKey, alt: e.altKey, shift: e.shiftKey, meta: e.metaKey });
  };
  const onPaste = (e) => {
    const s = e.clipboardData?.getData('text');
    if (s) { e.preventDefault(); send({ t: 'text', s: s.slice(0, 2000) }); }
  };

  const endSession = async () => {
    setEnding(true);
    try {
      await adminApi.post(`/remote/support/manager/session/${sid}/end`);
      toast.success(t('grv.ended', { defaultValue: 'Sessione terminata' }));
      navigate('/gestore/assistenza-remota');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('grv.endError', { defaultValue: 'Impossibile terminare la sessione' }));
    } finally { setEnding(false); }
  };

  const controlAvailable = meta ? meta.control !== false : true;
  const isEnded = wsState === 'ended';

  // Messaggio di stato al centro quando non ci sono frame
  let placeholder = null;
  if (isEnded) placeholder = { icon: Power, text: t('grv.sessionEnded', { defaultValue: 'La sessione è terminata.' }) };
  else if (wsState !== 'open') placeholder = { icon: Loader2, spin: true, text: t('grv.connecting', { defaultValue: 'Connessione al relay…' }) };
  else if (!agentOnline) placeholder = { icon: WifiOff, text: t('grv.waitingAgent', { defaultValue: 'In attesa del PC del locale… Assicurati che sul POS sia in esecuzione "avvia-remoto.bat" (cartella remote dell\'agente).' }) };
  else if (!hasFrame) placeholder = { icon: Loader2, spin: true, text: t('grv.waitingFrames', { defaultValue: 'Agente collegato, ricezione immagini…' }) };

  return (
    <div className="flex flex-col h-dvh" data-testid="gestore-remote-viewer">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 md:px-4 h-14 border-b border-border bg-card/80 backdrop-blur shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate('/gestore/assistenza-remota')} data-testid="grv-back" aria-label={t('common.back', { defaultValue: 'Indietro' })}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"><Building2 className="h-4 w-4" /></span>
        <div className="min-w-0 mr-2">
          <div className="font-semibold text-sm truncate" data-testid="grv-restaurant-name">{session?.restaurant_name || '…'}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {meta ? `${meta.w}×${meta.h}` : ''}{meta?.host ? ` · ${meta.host}` : ''}
          </div>
        </div>

        <Badge variant="outline" className={cn('gap-1 hidden sm:inline-flex', isEnded ? 'border-border text-muted-foreground' : agentOnline ? 'border-emerald-500/40 text-emerald-600' : 'border-amber-500/40 text-amber-600')} data-testid="grv-status">
          {isEnded ? <Power className="h-3.5 w-3.5" /> : agentOnline ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {isEnded ? t('grv.stEnded', { defaultValue: 'Terminata' }) : agentOnline ? t('grv.stOnline', { defaultValue: 'PC collegato' }) : t('grv.stWaiting', { defaultValue: 'In attesa del PC' })}
        </Badge>
        <Badge variant="outline" className="font-mono hidden md:inline-flex" data-testid="grv-fps">{fps} fps</Badge>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <div className="flex items-center gap-2" title={controlAvailable ? '' : t('grv.controlUnavailable', { defaultValue: 'Controllo non disponibile su questo PC' })}>
            <MousePointer2 className={cn('h-4 w-4', control && controlAvailable ? 'text-primary' : 'text-muted-foreground')} />
            <Switch id="grv-control" checked={control && controlAvailable} disabled={!controlAvailable || isEnded} onCheckedChange={setControl} data-testid="grv-control-toggle" />
            <Label htmlFor="grv-control" className="text-xs hidden sm:block cursor-pointer">{t('grv.control', { defaultValue: 'Controllo' })}</Label>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen} data-testid="grv-fullscreen" aria-label="Schermo intero">
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button variant="destructive" size="sm" onClick={endSession} disabled={ending || isEnded} data-testid="grv-end">
            {ending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Power className="h-4 w-4 mr-1" />}
            {t('grv.end', { defaultValue: 'Termina' })}
          </Button>
        </div>
      </div>

      {/* Area schermo */}
      <div ref={wrapRef} className="relative flex-1 min-h-0 bg-neutral-950 select-none" data-testid="grv-screen">
        <img
          ref={imgRef}
          alt=""
          draggable={false}
          className={cn('absolute inset-0 h-full w-full object-contain', !hasFrame && 'opacity-0')}
          data-testid="grv-frame"
        />
        {/* Overlay che cattura mouse/tastiera */}
        <div
          ref={overlayRef}
          tabIndex={0}
          role="application"
          aria-label={t('grv.overlayLabel', { defaultValue: 'Schermo remoto (clicca per prendere il controllo)' })}
          className={cn('absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-primary/60', control && controlAvailable && agentOnline && !isEnded ? 'cursor-crosshair' : 'cursor-default')}
          onMouseMove={onMouseMove}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onWheel={onWheel}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={onKey('kd')}
          onKeyUp={onKey('ku')}
          onPaste={onPaste}
          data-testid="grv-overlay"
        />
        {placeholder && (
          <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
            <div className="max-w-md text-center rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md px-6 py-8 text-neutral-200" data-testid="grv-placeholder">
              <placeholder.icon className={cn('h-10 w-10 mx-auto mb-3 text-neutral-400', placeholder.spin && 'animate-spin')} />
              <p className="text-sm">{placeholder.text}</p>
            </div>
          </div>
        )}
        {hasFrame && control && controlAvailable && !isEnded && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-[11px] text-neutral-200 pointer-events-none" data-testid="grv-control-hint">
            <Keyboard className="h-3.5 w-3.5" /> {t('grv.controlHint', { defaultValue: 'Clicca sullo schermo per inviare mouse e tastiera' })}
          </div>
        )}
        {!hasFrame && !placeholder && (
          <div className="absolute inset-0 flex items-center justify-center text-neutral-500"><MonitorUp className="h-10 w-10" /></div>
        )}
      </div>
    </div>
  );
}
